// Shared UI for the HPU and TEBA Payment Receive pages (same look on both).
import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LinkIcon from "@mui/icons-material/Link";
import PrintIcon from "@mui/icons-material/Print";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { HPC_COLORS } from "../../hpc/hpcTheme";
import { displayValue } from "./paymentUtils";

export const Field = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
      {displayValue(value)}
    </Typography>
  </Grid>
);

export const Stat = ({ label, value, highlight = false }) => (
  <Grid item xs={6} md={3}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography
      variant={highlight ? "h5" : "body1"}
      sx={{
        fontWeight: highlight ? 800 : 600,
        color: highlight ? HPC_COLORS.hpuOrange : "text.primary",
        wordBreak: "break-word",
      }}
    >
      {displayValue(value)}
    </Typography>
  </Grid>
);

export const FormSection = ({ title, children }) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography
      variant="overline"
      sx={{ display: "block", color: HPC_COLORS.hpuOrange, fontWeight: 700, letterSpacing: 1, mb: 0.75 }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

export const AmountField = ({ value, onChange, error, helperText }) => (
  <TextField
    fullWidth
    required
    type="text"
    inputMode="decimal"
    placeholder="0.00"
    value={value}
    onChange={onChange}
    error={Boolean(error)}
    helperText={error || helperText}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <Typography sx={{ fontWeight: 700, color: HPC_COLORS.hpuOrange }}>LKR</Typography>
        </InputAdornment>
      ),
      sx: { fontSize: "1.75rem", fontWeight: 700 },
    }}
    inputProps={{ "aria-label": "Payment amount" }}
  />
);

export const MethodButtons = ({ value, onChange, error }) => (
  <>
    <ToggleButtonGroup
      exclusive
      fullWidth
      value={value}
      onChange={onChange}
      aria-label="Payment method"
      sx={{
        "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, py: 1 },
        "& .MuiToggleButton-root.Mui-selected": {
          color: "#1a1200",
          backgroundColor: HPC_COLORS.hpuOrange,
          "&:hover": { backgroundColor: "#ffb84d" },
        },
      }}
    >
      <ToggleButton value="cash">Cash</ToggleButton>
      <ToggleButton value="bank_transfer">Bank Transfer</ToggleButton>
      <ToggleButton value="card">Card Payment</ToggleButton>
    </ToggleButtonGroup>
    {error && <Typography variant="caption" color="error">{error}</Typography>}
  </>
);

export const ReferenceField = ({ value, onChange, error }) => (
  <FormSection title="Reference">
    <TextField
      fullWidth
      required
      label="Payment reference"
      value={value}
      onChange={onChange}
      error={Boolean(error)}
      helperText={error || "Bank transfer or card reference"}
    />
  </FormSection>
);

export const DateField = ({ value, onChange, error, max }) => (
  <FormSection title="Date">
    <TextField
      fullWidth
      required
      label="Payment date"
      type="date"
      value={value}
      onChange={onChange}
      error={Boolean(error)}
      helperText={error}
      InputLabelProps={{ shrink: true }}
      inputProps={max ? { max } : undefined}
      sx={{ maxWidth: { sm: 280 } }}
    />
  </FormSection>
);

export const ReceivePaymentButton = ({ onClick, disabled }) => (
  <Button
    fullWidth
    size="large"
    variant="contained"
    color="success"
    onClick={onClick}
    disabled={disabled}
    sx={{ py: 1.5, fontWeight: 700, fontSize: "1rem" }}
  >
    Receive payment
  </Button>
);

/** Confirm step: review the details, then save. */
export const ConfirmPaymentDialog = ({ open, title, onClose, onConfirm, submitting, children, cancelColor }) => (
  <Dialog open={open} onClose={() => !submitting && onClose()} fullWidth maxWidth="sm">
    <DialogTitle>{title}</DialogTitle><DialogContent dividers><Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Review the details below. The payment will be saved only after you confirm.</Typography><Grid container spacing={1.5}>{children}</Grid></DialogContent>
    <DialogActions><Button onClick={onClose} disabled={submitting} color={cancelColor}>Cancel</Button><Button onClick={onConfirm} variant="contained" color="success" disabled={submitting} startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}>{submitting ? "Saving..." : "Confirm Payment"}</Button></DialogActions>
  </Dialog>
);

/** View / Print / PDF (and optional Copy link) buttons shown after a payment is saved. */
export const ReceiptActionButtons = ({ onView, onPrint, onPdf, onCopyLink, disabled }) => (
  <Box display="flex" gap={1} flexWrap="wrap" mt={1.5}>
    <Button size="small" variant="outlined" startIcon={<VisibilityIcon />} onClick={onView} disabled={disabled}>View Receipt</Button>
    <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={onPrint} disabled={disabled}>Print Receipt</Button>
    <Button size="small" variant="contained" startIcon={<DownloadIcon />} onClick={onPdf} disabled={disabled}>Download PDF</Button>
    {onCopyLink && (
      <Button size="small" variant="outlined" startIcon={<LinkIcon />} onClick={onCopyLink} disabled={disabled}>Copy Link</Button>
    )}
  </Box>
);

/** Small View / Print / PDF buttons for one row of the recent payments table. */
export const RowReceiptButtons = ({ onView, onPrint, onPdf, disabled }) => (
  <Box display="flex" justifyContent="flex-end" gap={0.5} flexWrap="wrap">
    <Button size="small" startIcon={<VisibilityIcon />} onClick={(event) => { event.stopPropagation(); onView(); }} disabled={disabled} sx={{ minHeight: 32, px: 1, color: HPC_COLORS.tebaBlue, borderColor: HPC_COLORS.tebaBlue }}>View</Button>
    <Button size="small" startIcon={<PrintIcon />} onClick={(event) => { event.stopPropagation(); onPrint(); }} disabled={disabled} sx={{ minHeight: 32, px: 1, color: HPC_COLORS.hpuOrange, borderColor: HPC_COLORS.hpuOrange }}>Print</Button>
    <Button size="small" startIcon={<DownloadIcon />} variant="contained" onClick={(event) => { event.stopPropagation(); onPdf(); }} disabled={disabled} sx={{ minHeight: 32, px: 1, color: "#102018", backgroundColor: "#4cceac" }}>PDF</Button>
  </Box>
);

/**
 * Collapsible "Recent payments" card. ``headers``: column titles (the last one is right-aligned);
 * ``renderCells(row)`` returns the row's cells.
 */
export const RecentPaymentsCard = ({
  rows, loaded, open, onToggle, headers, renderCells, rowKey, onRowClick, emptyText,
}) => (
  <Card>
    <CardContent sx={{ "&:last-child": { pb: 2 } }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={onToggle}
        sx={{ cursor: "pointer" }}
      >
        <Typography variant="h5" fontWeight={700}>
          Recent payments{loaded ? ` (${rows.length})` : ""}
        </Typography>
        <IconButton size="small" aria-label={open ? "Hide recent payments" : "Show recent payments"}>
          <ExpandMoreIcon sx={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
        </IconButton>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ mt: 1.5 }}>
          {rows.length === 0 ? <Alert severity="info">{emptyText}</Alert> : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small"><TableHead><TableRow>{headers.map((header, index) => <TableCell key={header} align={index === headers.length - 1 ? "right" : undefined}>{header}</TableCell>)}</TableRow></TableHead>
                <TableBody>{rows.map((row) => <TableRow key={rowKey(row)} hover onClick={onRowClick ? () => onRowClick(row) : undefined} sx={onRowClick ? { cursor: "pointer" } : undefined}>
                  {renderCells(row)}
                </TableRow>)}</TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Collapse>
    </CardContent>
  </Card>
);

export { TableCell };
